// @flow

import jscookie from 'js-cookie';
import {Categories} from './Categories';
import type {Category, PermissionsChangedCallback} from './types';

export const PERMISSIONS_COOKIE_NAME = 'cookie-perms';
export const PERMISSIONS_COOKIE_VERSION = '1';

// TODO (nkohari): Determine URLs which need to be called to set permission cookies
export const SET_PERMISSIONS_URLS = [];

/**
 * Represents a set of permissions set for a user. Normally, you shouldn't
 * need to work with this directly. Instead, you should use {Cookies} or {LocalStorage}.
 * @param {Date} timestamp The timestamp to use.
 * @param {Map} permissions The permissions map to use.
 */
export class Permissions {
  _timestamp: Date;
  _permissions: {[category: Category]: boolean};

  constructor(timestamp: Date, permissions: {[category: Category]: boolean}) {
    this._timestamp = timestamp;
    this._permissions = permissions;
  }

  /**
   * Returns the timestamp at which the permissions were set.
   */
  get timestamp() {
    return this._timestamp;
  }

  /**
   * Determines whether the specified storage category is allowed.
   * @param {Category} category The category in question
   * @returns True if items belonging to the category can be written, otherwise false.
   */
  get(category: Category) {
    return this._permissions[category];
  }

  /**
   * Returns a map of all permissions. The keys of the map are the storage categories,
   * and each value is a Boolean representing whether the category is allowed.
   * @returns The map of permissions.
   */
  getAll(): {[category: Category]: boolean} {
    return {...this._permissions};
  }

  /**
   * Serializes the Permissions object to a string which can be written to a cookie.
   */
  toString(): string {
    const tokens = Object.keys(Categories)
      .reduce((array, name) => {
        const category = Categories[name];
        return !category.necessary && this._permissions[name]
          ? [...array, category.token]
          : array;
      }, [])
      .sort();

    const seconds = Math.floor(this.timestamp.valueOf() / 1000);
    return `${PERMISSIONS_COOKIE_VERSION}:${seconds}|${tokens.join('')}`;
  }

  /**
   * Deserializes a Permissions object from a string representation.
   * @param {str} str The serialized representation
   * @returns The deserialized Permissions instance.
   */
  static deserialize(str: string): Permissions {
    const [version, payload] = str.split(':', 2);
    const segments = payload.split('|');

    const timestamp = new Date(Number(segments[0]) * 1000);
    const tokens = segments[1].split('');

    const permissions = Object.keys(Categories).reduce((hash, name) => {
      const category = Categories[name];
      if (category.necessary) {
        // If the category is considered necessary, permissions are implicit.
        hash[name] = true;
      } else {
        // Otherwise, check for the category's token in the permission list.
        hash[name] = tokens.indexOf(category.token) >= 0;
      }
      return hash;
    }, {});

    return new Permissions(timestamp, permissions);
  }

  /**
   * Gets the user's current permissions set, if one has been set.
   * @returns A Permissions instance representing the user's permissions.
   */
  static get(): Permissions | null {
    try {
      // Try to read and deserialize permissions from the permissions cookie.
      // We avoid caching the deserialized permissions to ensure that we're
      // always in sync with the cookie; for example, it's possible that the
      // user makes a change to the cookie in a different tab.
      const str = jscookie.get(PERMISSIONS_COOKIE_NAME);
      if (str) return Permissions.deserialize(str);
    } catch (err) {
      // If we encounter any errors trying to deserialize the permissions,
      // treat them as though they don't exist.
    }

    return null;
  }

  /**
   * Sets the specified permissions for the user by making fetch() calls to the cookie
   * permissions API endpoints.
   * @param {Map} changedPermissions The permissions to change
   * @returns A promise for a Permissions instance representing the new permissions.
   */
  static async set(changes: {
    [category: Category]: boolean,
  }): Promise<Permissions | null> {
    // Because ITP >2.1 restricts the lifetime of cookies set via JS to a maximum of
    // 7 days, the permissions cookies need to be set on the server instead. Also,
    // because we need to control cookies being set on several TLDs, we need to make
    // calls to multiple endpoints to set these cookies.
    const promises = SET_PERMISSIONS_URLS.map((url) => {
      return fetch(url, {
        method: 'post',
        body: JSON.stringify(changes),
        headers: {'Content-Type': 'application/json'},
      });
    });

    // The server will set the new cookie, so we wait for the requests to complete
    // and then look at the value of the cookie to get the new permissions.
    // TODO (nkohari): Ensure that if one of the requests errors, the rest continue.
    await Promise.all(promises);

    return Permissions.get();
  }

  /**
   * Set cookie permissions for the user, allowing all cookies.
   * @returns A promise for a Permissions instance representing the new permissions.
   */
  static async allowAll(): Promise<Permissions | null> {
    const permissions = Object.keys(Categories).reduce((hash, name) => {
      const category = Categories[name];
      if (!category.necessary) hash[name] = true;
      return hash;
    }, {});

    return Permissions.set(permissions);
  }

  /**
   * Set cookie permissions for the user, blocking all unnecessary cookies.
   * @returns A promise for a Permissions instance representing the new permissions.
   */
  static async rejectAll(): Promise<Permissions | null> {
    const permissions = Object.keys(Categories).reduce((hash, name) => {
      const category = Categories[name];
      if (!category.necessary) hash[name] = false;
      return hash;
    }, {});

    return Permissions.set(permissions);
  }
}
